import { FunctionComponent, ReactNode } from 'react'
import { useParams, useRouteMatch } from 'react-router-dom'
import { ExpandMore } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@black-bear-energy/rucksack'
import { ChecklistCategoryAPIResponse } from '@black-bear-energy/black-bear-energy-common'
import { useCustomerRFPDocumentSummary } from '../../api/hooks/useCustomerRFPDocumentSummary'
import { useSiteRFPDocumentExchangeDetails } from '../../api/hooks/useSiteRFPDocumentExchangeDetails'
import { useSiteRFPDocumentSummary } from '../../api/hooks/useSiteRFPDocumentSummary'
import { ArrayLocationChip } from '../common/ArrayLocationChip'
import { Field } from './Field'
import { SiteRFPDetailCard } from './SiteRFPDetailCard'
import { SiteRFPPhaseChips } from './SiteRFPPhaseChips'
import { useDocumentExchangeHeaderBreadcrumbs } from './useDocumentExchangeHeaderBreadcrumbs'
import { formatTableDate } from '../helpers/formatTableDate'
import { formatSystemSize } from '../helpers/formatSystemSize'

interface SiteRFPDocumentExchangeLayoutProps {
  children: ReactNode
}

export const SiteRFPDocumentExchangeLayout: FunctionComponent<
  SiteRFPDocumentExchangeLayoutProps
> = ({ children }) => {
  const { customerRFPId, siteRFPId } = useParams<{
    customerRFPId: string
    siteRFPId: string
  }>()

  const checklistCategory = useChecklistCategoryFromRoute(
    customerRFPId,
    siteRFPId
  )

  const {
    isPending: isPendingCustomerRFPDocumentSummary,
    data: customerRFPDocumentSummary,
  } = useCustomerRFPDocumentSummary(Number(customerRFPId))
  const {
    isPending: isPendingSiteRFPDocumentSummary,
    data: siteRFPDocumentSummary,
  } = useSiteRFPDocumentSummary(Number(siteRFPId))
  const { data: siteRFPDocumentExchangeDetails } =
    useSiteRFPDocumentExchangeDetails(Number(siteRFPId))

  const inlineBreadcrumbs = useDocumentExchangeHeaderBreadcrumbs({
    projectBreadcrumbProps: {
      customerRFPName: customerRFPDocumentSummary?.customerRFPName,
      isCustomerRFPNamePending: isPendingCustomerRFPDocumentSummary,
    },
    siteBreadcrumbProps: {
      customerRFPId,
      siteRFPId,
      siteRFPName: siteRFPDocumentSummary?.name,
      isSiteRFPNamePending: isPendingSiteRFPDocumentSummary,
    },
    checklistCategory,
  })

  return (
    <>
      {inlineBreadcrumbs}

      <SiteRFPDetailCard variant="outlined">
        <Accordion
          elevation={0}
          defaultExpanded
          sx={{ backgroundColor: 'inherit' }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              padding: 0,
              minHeight: 0,
              '&.Mui-expanded': {
                minHeight: 0,
              },
              '& .MuiAccordionSummary-content': {
                margin: 0,
              },
              '& .MuiAccordionSummary-content.Mui-expanded': {
                margin: 0,
              },
            }}
          >
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="subtitle1">
                {siteRFPDocumentSummary?.name}
              </Typography>
              {siteRFPDocumentSummary && (
                <SiteRFPPhaseChips
                  phase={siteRFPDocumentSummary.phase}
                  scale="small"
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              paddingTop: 2,
              paddingBottom: 0,
              paddingX: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2">
                  {siteRFPDocumentSummary?.address}
                </Typography>
                <Typography variant="body2">
                  {siteRFPDocumentSummary?.cityStateZip}
                </Typography>
              </Box>
              <Field label="Section">{siteRFPDocumentSummary?.section}</Field>
              <Field label="Array Location">
                {siteRFPDocumentExchangeDetails?.arrayLocation && (
                  <ArrayLocationChip
                    arrayLocation={
                      siteRFPDocumentExchangeDetails?.arrayLocation
                    }
                  />
                )}
              </Field>
              <Field label="NTP Estimate">
                {siteRFPDocumentSummary?.ntpEstimate &&
                  formatTableDate(siteRFPDocumentSummary?.ntpEstimate)}
              </Field>
              <Field label="System Size">
                {siteRFPDocumentSummary?.systemSize &&
                  formatSystemSize(siteRFPDocumentSummary?.systemSize)}
              </Field>
              <Field label="Utility">
                {siteRFPDocumentExchangeDetails?.utility}
              </Field>
              <Field label="Program">
                {siteRFPDocumentExchangeDetails?.program}
              </Field>
              <Field label="Channel Partner">
                {siteRFPDocumentSummary?.channelPartnerName}
              </Field>
            </Box>
          </AccordionDetails>
        </Accordion>
      </SiteRFPDetailCard>

      {children}
    </>
  )
}

function useChecklistCategoryFromRoute(
  customerRFPId: string,
  siteRFPId: string
): ChecklistCategoryAPIResponse | undefined {
  const basePath = `/document-exchange/projects/${customerRFPId}/sites/${siteRFPId}`
  const contractsMatch = useRouteMatch({ path: `${basePath}/contracts` })
  const constructionMatch = useRouteMatch({ path: `${basePath}/construction` })
  if (contractsMatch) return ChecklistCategoryAPIResponse.Contracts
  if (constructionMatch) return ChecklistCategoryAPIResponse.Construction
  return undefined
}
